import { FormattedMessage, useIntl } from "react-intl";

import { DeleteOutlined, UploadOutlined } from "@ant-design/icons";

import { Button, Flex, Modal, Space, Upload } from "antd";
import { UploadChangeParam } from "antd/es/upload/interface";

import { CmtUserAvatar } from "@app/components/users/field/CmtUserAvatar.tsx";
import { User } from "@app/models/User.ts";
import { useAvatar } from "@app/services/hook/useAvatar.tsx";
import { showNotification } from "@app/services/notification/showNotification.tsx";

interface CmtAvatarModalProps {
  open: boolean;
  handleCancel: () => void;
  user: User;
}

const fileType = ["image/jpeg", "image/png"];
const MAX_AVATAR_SIZE = 10 ** 6;

export const CmtAvatarModal = ({
  open,
  handleCancel,
  user,
}: CmtAvatarModalProps) => {
  const intl = useIntl();
  const { uploadAvatar, deleteAvatar } = useAvatar(user.id);

  const handleUploadAvatar = (info: UploadChangeParam) => {
    const file = info.fileList[info.fileList.length - 1].originFileObj as File;

    if (!fileType.includes(file.type)) {
      showNotification({
        type: "error",
        title: intl.formatMessage({
          id: "error.avatar.type.title",
          defaultMessage: "Error",
        }),
        description: intl.formatMessage({
          id: "error.avatar.type.description",
          defaultMessage: "File type must be JPEG or PNG!",
        }),
      });
      return;
    }

    if (file.size > MAX_AVATAR_SIZE) {
      showNotification({
        type: "error",
        title: intl.formatMessage({
          id: "error.avatar.size.title",
          defaultMessage: "Error",
        }),
        description: intl.formatMessage({
          id: "error.avatar.size.description",
          defaultMessage:
            "File too large. Please upload a file smaller than 1MB.",
        }),
      });
      return;
    }

    uploadAvatar.mutate(file);
  };

  const handleDeleteAvatar = () => {
    deleteAvatar.mutate();
  };

  return (
    <Modal
      open={open}
      onCancel={handleCancel}
      title={
        <FormattedMessage
          id="collaborator.avatar.edit"
          defaultMessage="Change avatar"
        />
      }
      footer={
        <Flex justify={"center"}>
          <Space size={"large"} style={{ marginTop: "1.5rem" }}>
            <Upload
              multiple={false}
              onChange={handleUploadAvatar}
              showUploadList={false}
              beforeUpload={() => false}
            >
              <Button
                type="primary"
                icon={<UploadOutlined />}
                loading={uploadAvatar.isPending}
              >
                <span>
                  <FormattedMessage
                    id={"collaborator.avatar.upload"}
                    defaultMessage={"Upload new avatar"}
                  />
                </span>
              </Button>
            </Upload>
            <Button
              danger
              icon={<DeleteOutlined />}
              disabled={!user.avatarUrl}
              loading={deleteAvatar.isPending}
              onClick={handleDeleteAvatar}
            >
              <span>
                <FormattedMessage
                  id={"collaborator.avatar.delete"}
                  defaultMessage={"Delete avatar"}
                />
              </span>
            </Button>
          </Space>
        </Flex>
      }
    >
      <Flex justify={"center"}>
        <CmtUserAvatar user={user} size={280} />
      </Flex>
    </Modal>
  );
};
